import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Clock,
  Home,
  Loader2,
  RefreshCw,
  XCircle,
} from 'lucide-react';
import React, { useEffect, useState, useRef } from 'react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ComplianceCompanyConfig } from '@/constants/compliance-companies';
import api from '@/utils/ky';

interface ComplianceFeedbackProps {
  config: ComplianceCompanyConfig;
  callDuration: number;
  transcript: Array<{
    source: 'ai' | 'user';
    message: string;
    timestamp: Date;
  }>;
  onStartNewSession: () => void;
  onBackToLanding: () => void;
}

interface ComplianceAssessment {
  overallScore: number;
  categories: {
    crossBorderCompliance: {
      score: number;
      feedback: string;
      violations: string[];
    };
    mnpiHandling: {
      score: number;
      feedback: string;
      violations: string[];
    };
    communicationProtocols: {
      score: number;
      feedback: string;
      violations: string[];
    };
    clientRelationship: {
      score: number;
      feedback: string;
      strengths: string[];
    };
    postCallAction?: {
      score: number;
      feedback: string;
      correct: boolean;
      selectedAnswer?: string;
      correctAnswer?: string;
    };
  };
  keyMoments: Array<{
    timestamp: string;
    type: 'success' | 'warning' | 'error';
    description: string;
    userResponse?: string;
  }>;
  recommendations: string[];
}

export const ComplianceFeedback: React.FC<ComplianceFeedbackProps> = ({
  config,
  callDuration,
  transcript,
  onStartNewSession,
  onBackToLanding,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [assessment, setAssessment] = useState<ComplianceAssessment | null>(
    null,
  );
  const [showQuiz, setShowQuiz] = useState(true);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [apiResponseReady, setApiResponseReady] = useState(false);
  const [apiRequestSent, setApiRequestSent] = useState(false);
  const requestInProgress = useRef(false);

  const quizQuestion = {
    question: 'What do you do immediately after this client call?',
    options: [
      { id: 'A', text: 'report MNPI via the official MS compliance channels' },
      {
        id: 'B',
        text: 'tell your manager about what the client shared to ask for advice and be extremely sure whether it was MNPI or not',
      },
      {
        id: 'C',
        text: "ask the client to repeat the information over email as you are not sure if it's truly MNPI, and then forward it to compliance to check whether there was a breach",
      },
      { id: 'D', text: "do nothing since it's not really counted as MNPI" },
    ],
    correctAnswer: 'A',
  };

  useEffect(() => {
    // Prevent duplicate API calls (React StrictMode in dev can cause double calls)
    if (!apiRequestSent && !requestInProgress.current) {
      setApiRequestSent(true);
      fetchAssessment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const assessQuizLocally = (
    answer: string,
  ): { score: number; feedback: string; correct: boolean } => {
    const isCorrect = answer === quizQuestion.correctAnswer;
    return {
      score: isCorrect ? 100 : 0,
      correct: isCorrect,
      feedback: isCorrect
        ? 'Correct post-call action selected. Reporting MNPI exposure through official compliance channels is the proper protocol to protect both you and the firm.'
        : `Incorrect post-call action selected. You chose "${answer}" but the correct answer is "${quizQuestion.correctAnswer}". Remember, immediate reporting through official compliance channels is critical when MNPI has been disclosed.`,
    };
  };

  const calculateOverallScore = (
    categories: any,
    includeQuiz: boolean = false,
  ): number => {
    const weights = includeQuiz
      ? {
          crossBorderCompliance: 0.2,
          mnpiHandling: 0.25,
          communicationProtocols: 0.2,
          clientRelationship: 0.15,
          postCallAction: 0.2,
        }
      : {
          crossBorderCompliance: 0.25,
          mnpiHandling: 0.3,
          communicationProtocols: 0.25,
          clientRelationship: 0.2,
        };

    let weightedScore = 0;
    for (const [category, data] of Object.entries(categories) as any) {
      if (data && typeof data.score === 'number') {
        weightedScore += data.score * ((weights as any)[category] || 0);
      }
    }

    return Math.round(weightedScore);
  };

  const submitQuiz = () => {
    if (!selectedAnswer) return;
    setQuizSubmitted(true);

    // Assess quiz locally
    const quizResult = assessQuizLocally(selectedAnswer);

    // If API response is ready, merge and show results
    if (apiResponseReady && assessment) {
      const updatedAssessment = {
        ...assessment,
        categories: {
          ...assessment.categories,
          postCallAction: {
            ...quizResult,
            selectedAnswer,
            correctAnswer: quizQuestion.correctAnswer,
          },
        },
      };

      // Recalculate overall score with quiz included
      updatedAssessment.overallScore = calculateOverallScore(
        updatedAssessment.categories,
        true,
      );

      setAssessment(updatedAssessment);
      setShowQuiz(false);
      setIsLoading(false);
    } else {
      // API still loading, wait for it
      setIsLoading(true);
    }
  };

  const fetchAssessment = async () => {
    // Double-check to prevent duplicate calls
    if (apiResponseReady || requestInProgress.current) return;

    requestInProgress.current = true;

    try {
      setIsLoading(true);

      // Format transcript for API
      const formattedTranscript = transcript
        .map(
          (entry) =>
            `${
              entry.source === 'ai'
                ? config.scenario.participants.client.name
                : 'Eric'
            }: ${entry.message}`,
        )
        .join('\n');

      const data: any = await api
        .post('api/compliance/feedback', {
          json: {
            transcript: formattedTranscript,
            duration: callDuration,
            scenarioType: 'cross-border-ma',
          },
          timeout: 60000, // 60 second timeout
        })
        .json();

      if (!data) {
        throw new Error('Failed to fetch assessment');
      }

      setAssessment(data);
      setApiResponseReady(true);

      // If quiz is already submitted, merge results and show final assessment
      if (quizSubmitted && selectedAnswer) {
        const quizResult = assessQuizLocally(selectedAnswer);
        const updatedAssessment = {
          ...data,
          categories: {
            ...data.categories,
            postCallAction: {
              ...quizResult,
              selectedAnswer,
              correctAnswer: quizQuestion.correctAnswer,
            },
          },
        };

        updatedAssessment.overallScore = calculateOverallScore(
          updatedAssessment.categories,
          true,
        );
        setAssessment(updatedAssessment);
        setShowQuiz(false);
        setIsLoading(false);
      } else {
        // API ready, but quiz not submitted yet - keep showing quiz
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Error fetching assessment:', err);

      // Use mock data for development/fallback
      const mockAssessment = {
        overallScore: 78,
        categories: {
          crossBorderCompliance: {
            score: 85,
            feedback: "Excellent handling of the Taiwan travel request. You correctly identified the licensing restrictions and offered an appropriate alternative solution.",
            violations: []
          },
          mnpiHandling: {
            score: 70,
            feedback: "Good recognition of MNPI concerns when SanTech was mentioned. However, the response could have been more immediate and firm.",
            violations: ["Slight delay in addressing the MNPI disclosure"]
          },
          communicationProtocols: {
            score: 75,
            feedback: "Properly declined WeChat communication and maintained compliance with approved channels. Could have explained the rationale more clearly.",
            violations: []
          },
          clientRelationship: {
            score: 82,
            feedback: "Maintained professional and helpful demeanor throughout. Good balance between compliance requirements and client service.",
            strengths: ["Professional tone", "Clear explanations", "Offered alternatives"]
          }
        },
        keyMoments: [
          {
            timestamp: "0:45",
            type: "success" as const,
            description: "Travel request to Taiwan",
            userResponse: "Correctly explained licensing restrictions and offered Taiwan team support"
          },
          {
            timestamp: "1:30",
            type: "warning" as const,
            description: "MNPI disclosure about SanTech",
            userResponse: "Identified MNPI concern but could have been more immediate"
          },
          {
            timestamp: "2:15",
            type: "success" as const,
            description: "WeChat communication request",
            userResponse: "Properly declined and offered email alternative"
          }
        ],
        recommendations: [
          "Practice more immediate and firm responses to MNPI disclosures",
          "Develop clearer explanations for compliance requirements to help clients understand",
          "Consider documenting compliance concerns during the call for follow-up",
          "Review the firm's policies on cross-border activities regularly"
        ]
      };

      setAssessment(mockAssessment);
      setApiResponseReady(true);

      // Handle quiz if it was already submitted
      if (quizSubmitted && selectedAnswer) {
        const quizResult = assessQuizLocally(selectedAnswer);
        const updatedAssessment = {
          ...mockAssessment,
          categories: {
            ...mockAssessment.categories,
            postCallAction: {
              ...quizResult,
              selectedAnswer,
              correctAnswer: quizQuestion.correctAnswer,
            },
          },
        };
        updatedAssessment.overallScore = calculateOverallScore(
          updatedAssessment.categories,
          true,
        );
        setAssessment(updatedAssessment);
        setShowQuiz(false);
      }
    } finally {
      setIsLoading(false);
      requestInProgress.current = false;
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  if (showQuiz) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Header */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src={config.logo} alt={config.name} className="h-10" />
              <div className="border-l border-gray-300 pl-4">
                <span className="text-sm font-medium text-gray-700">
                  Training Assessment
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Powered by</span>
              <img src={config.hupoLogo} alt="Hupo" className="h-6" />
            </div>
          </div>
        </div>

        {/* Quiz */}
        <div className="max-w-3xl mx-auto px-6 py-12">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">
              Final Assessment Question
            </h1>
            <p className="text-gray-600">
              Answer this question based on the conversation you just had
            </p>
          </div>

          <Card className="p-6 bg-white shadow-sm">
            <h2 className="text-lg font-medium text-gray-900 mb-6">
              {quizQuestion.question}
            </h2>
            <div className="space-y-3">
              {quizQuestion.options.map((option) => (
                <label
                  key={option.id}
                  className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                    selectedAnswer === option.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="quiz-answer"
                    value={option.id}
                    checked={selectedAnswer === option.id}
                    onChange={(e) => setSelectedAnswer(e.target.value)}
                    className="mt-0.5"
                  />
                  <div>
                    <span className="font-medium text-gray-900">
                      {option.id})
                    </span>{' '}
                    <span className="text-gray-700">{option.text}</span>
                  </div>
                </label>
              ))}
            </div>
            <div className="mt-6 text-center">
              <Button
                onClick={submitQuiz}
                disabled={!selectedAnswer}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8"
              >
                Submit Answer
              </Button>
            </div>

            {/* Loading indicator for API call */}
            {!apiResponseReady && (
              <div className="mt-4 text-center">
                <div className="inline-flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Processing conversation analysis...</span>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <Card className="p-8 text-center bg-white shadow-sm">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-blue-600" />
          <h2 className="text-xl font-semibold mb-2">Analyzing Performance</h2>
          <p className="text-gray-600">
            Evaluating compliance handling and quiz response...
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={config.logo} alt={config.name} className="h-10" />
            <div className="border-l border-gray-300 pl-4">
              <span className="text-sm font-medium text-gray-700">
                Training Complete
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>Powered by</span>
            <img src={config.hupoLogo} alt="Hupo" className="h-6" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Overall Score - Minimalist */}
        <div className="text-center mb-10">
          <div
            className={`inline-flex items-center justify-center w-28 h-28 rounded-full border-4 ${
              assessment && assessment.overallScore >= 80
                ? 'border-green-500'
                : assessment && assessment.overallScore >= 60
                ? 'border-amber-500'
                : 'border-red-500'
            } bg-white shadow-md mb-4`}
          >
            <span
              className={`text-3xl font-bold ${
                assessment
                  ? getScoreColor(assessment.overallScore)
                  : 'text-gray-600'
              }`}
            >
              {assessment?.overallScore || 0}%
            </span>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-1">
            Performance Assessment
          </h1>
          <p className="text-sm text-gray-500">
            {formatDuration(callDuration)} • {transcript.length} exchanges
          </p>
        </div>

        {/* Category Scores - Minimal Cards */}
        <div
          className={`grid ${
            assessment?.categories.postCallAction
              ? 'grid-cols-5'
              : 'grid-cols-4'
          } gap-3 mb-8`}
        >
          {assessment && (
            <>
              <Card className="p-4 text-center bg-white border-0 shadow-sm">
                <div
                  className={`text-2xl font-bold ${getScoreColor(
                    assessment.categories.crossBorderCompliance.score,
                  )}`}
                >
                  {assessment.categories.crossBorderCompliance.score}%
                </div>
                <p className="text-xs text-gray-500 font-medium mt-1">
                  CROSS-BORDER
                </p>
              </Card>
              <Card className="p-4 text-center bg-white border-0 shadow-sm">
                <div
                  className={`text-2xl font-bold ${getScoreColor(
                    assessment.categories.mnpiHandling.score,
                  )}`}
                >
                  {assessment.categories.mnpiHandling.score}%
                </div>
                <p className="text-xs text-gray-500 font-medium mt-1">MNPI</p>
              </Card>
              <Card className="p-4 text-center bg-white border-0 shadow-sm">
                <div
                  className={`text-2xl font-bold ${getScoreColor(
                    assessment.categories.communicationProtocols.score,
                  )}`}
                >
                  {assessment.categories.communicationProtocols.score}%
                </div>
                <p className="text-xs text-gray-500 font-medium mt-1">
                  PROTOCOLS
                </p>
              </Card>
              <Card className="p-4 text-center bg-white border-0 shadow-sm">
                <div
                  className={`text-2xl font-bold ${getScoreColor(
                    assessment.categories.clientRelationship.score,
                  )}`}
                >
                  {assessment.categories.clientRelationship.score}%
                </div>
                <p className="text-xs text-gray-500 font-medium mt-1">
                  CLIENT MGMT
                </p>
              </Card>

              {/* Quiz Score Card */}
              {assessment.categories.postCallAction && (
                <Card className="p-4 text-center bg-white border-0 shadow-sm">
                  <div
                    className={`text-2xl font-bold ${getScoreColor(
                      assessment.categories.postCallAction.score,
                    )}`}
                  >
                    {assessment.categories.postCallAction.score}%
                  </div>
                  <p className="text-xs text-gray-500 font-medium mt-1">
                    POST-CALL
                  </p>
                </Card>
              )}
            </>
          )}
        </div>

        {/* Simplified Tabs */}
        <Tabs defaultValue="moments" className="space-y-4">
          <TabsList className="grid w-full grid-cols-3 h-10">
            <TabsTrigger value="moments" className="text-sm">
              Key Moments
            </TabsTrigger>
            <TabsTrigger value="feedback" className="text-sm">
              Feedback
            </TabsTrigger>
            <TabsTrigger value="next" className="text-sm">
              Next Steps
            </TabsTrigger>
          </TabsList>

          <TabsContent value="moments" className="space-y-3">
            {assessment?.keyMoments.map((moment, index) => (
              <Card key={index} className="p-4 bg-white shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    {moment.type === 'success' && (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    )}
                    {moment.type === 'warning' && (
                      <AlertTriangle className="w-5 h-5 text-amber-500" />
                    )}
                    {moment.type === 'error' && (
                      <XCircle className="w-5 h-5 text-red-500" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-3 h-3 text-gray-400" />
                      <span className="text-xs text-gray-500">
                        {moment.timestamp}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-900">
                      {moment.description}
                    </p>
                    {moment.userResponse && (
                      <p className="text-xs text-gray-600 mt-1">
                        {moment.userResponse}
                      </p>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="feedback" className="space-y-4">
            {assessment && (
              <>
                <Card className="p-5 bg-white shadow-sm">
                  <h3 className="font-medium text-gray-900 mb-3">
                    Cross-Border Compliance
                  </h3>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2 mr-3">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{
                          width: `${assessment.categories.crossBorderCompliance.score}%`,
                        }}
                      />
                    </div>
                    <span
                      className={`text-sm font-bold ${getScoreColor(
                        assessment.categories.crossBorderCompliance.score,
                      )}`}
                    >
                      {assessment.categories.crossBorderCompliance.score}%
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {assessment.categories.crossBorderCompliance.feedback}
                  </p>
                </Card>

                <Card className="p-5 bg-white shadow-sm">
                  <h3 className="font-medium text-gray-900 mb-3">
                    MNPI Handling
                  </h3>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2 mr-3">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{
                          width: `${assessment.categories.mnpiHandling.score}%`,
                        }}
                      />
                    </div>
                    <span
                      className={`text-sm font-bold ${getScoreColor(
                        assessment.categories.mnpiHandling.score,
                      )}`}
                    >
                      {assessment.categories.mnpiHandling.score}%
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {assessment.categories.mnpiHandling.feedback}
                  </p>
                  {assessment.categories.mnpiHandling.violations.length > 0 && (
                    <div className="mt-2 p-2 bg-amber-50 rounded text-xs text-amber-700">
                      {assessment.categories.mnpiHandling.violations[0]}
                    </div>
                  )}
                </Card>

                <Card className="p-5 bg-white shadow-sm">
                  <h3 className="font-medium text-gray-900 mb-3">
                    Communication Protocols
                  </h3>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2 mr-3">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{
                          width: `${assessment.categories.communicationProtocols.score}%`,
                        }}
                      />
                    </div>
                    <span
                      className={`text-sm font-bold ${getScoreColor(
                        assessment.categories.communicationProtocols.score,
                      )}`}
                    >
                      {assessment.categories.communicationProtocols.score}%
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {assessment.categories.communicationProtocols.feedback}
                  </p>
                </Card>

                <Card className="p-5 bg-white shadow-sm">
                  <h3 className="font-medium text-gray-900 mb-3">
                    Client Relationship
                  </h3>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2 mr-3">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{
                          width: `${assessment.categories.clientRelationship.score}%`,
                        }}
                      />
                    </div>
                    <span
                      className={`text-sm font-bold ${getScoreColor(
                        assessment.categories.clientRelationship.score,
                      )}`}
                    >
                      {assessment.categories.clientRelationship.score}%
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {assessment.categories.clientRelationship.feedback}
                  </p>
                  {assessment.categories.clientRelationship.strengths.length >
                    0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {assessment.categories.clientRelationship.strengths.map(
                        (strength, i) => (
                          <span
                            key={i}
                            className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded"
                          >
                            {strength}
                          </span>
                        ),
                      )}
                    </div>
                  )}
                </Card>

                {/* Quiz Assessment Card */}
                {assessment.categories.postCallAction && (
                  <Card className="p-5 bg-white shadow-sm">
                    <h3 className="font-medium text-gray-900 mb-3">
                      Post-Call Action
                    </h3>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2 mr-3">
                        <div
                          className="bg-blue-500 h-2 rounded-full"
                          style={{
                            width: `${assessment.categories.postCallAction.score}%`,
                          }}
                        />
                      </div>
                      <span
                        className={`text-sm font-bold ${getScoreColor(
                          assessment.categories.postCallAction.score,
                        )}`}
                      >
                        {assessment.categories.postCallAction.score}%
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      {assessment.categories.postCallAction.feedback}
                    </p>
                    <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
                      <strong>Your Answer:</strong>{' '}
                      {assessment.categories.postCallAction.selectedAnswer})
                      {
                        quizQuestion.options.find(
                          (opt) =>
                            opt.id ===
                            assessment.categories.postCallAction.selectedAnswer,
                        )?.text
                      }
                      <br />
                      <strong>Correct Answer:</strong>{' '}
                      {assessment.categories.postCallAction.correctAnswer})
                      {
                        quizQuestion.options.find(
                          (opt) =>
                            opt.id ===
                            assessment.categories.postCallAction.correctAnswer,
                        )?.text
                      }
                    </div>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="next" className="space-y-4">
            <Card className="p-5 bg-white shadow-sm">
              <h3 className="font-medium text-gray-900 mb-3">
                Recommendations
              </h3>
              <div className="space-y-2">
                {assessment?.recommendations.map((rec, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-700">{rec}</p>
                  </div>
                ))}
              </div>
            </Card>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={onStartNewSession}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
              <Button
                variant="outline"
                onClick={onBackToLanding}
                className="flex-1"
              >
                <Home className="w-4 h-4 mr-2" />
                Home
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
